import React, { useEffect, useState } from 'react'
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts'
import { C, Track, Mono, Empty, Avatar } from '../components/ui'
import { getScorecardSummary, getScorecardTrend, getTwoLegRecord, todayStr } from '../api/client'

const PLAYS_KEY = 'linebrkr_plays'
function loadPlays() { try { return JSON.parse(localStorage.getItem(PLAYS_KEY) || '[]') } catch { return [] } }

const PROP_LABEL = {
  'HRR_over_1.5': 'HRR',
  'HRR':          'HRR',
  'strikeouts':   'Strikeouts',
  'total_bases':  'Total Bases',
  'HR_anytime':   'Home Run',
  'doubles':      'Doubles',
}

const fmtRoi = (v) => {
  if (v == null || isNaN(v)) return '—'
  const pct = v * 100
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`
}

const fmtChartDate = (iso) => {
  if (!iso) return ''
  const [, m, d] = iso.split('-')
  return `${parseInt(m, 10)}/${parseInt(d, 10)}`
}

// ── Market Drilldown ──────────────────────────────────────────────────────────
function MarketDrilldown({ market, plays, onClose }) {
  const mPlays   = plays.filter(p => (p.market || '').toLowerCase().includes(market.toLowerCase()))
  const finished = mPlays.filter(p => ['won','lost','push'].includes(p.status))
  const wins     = finished.filter(p => p.status === 'won').length
  const pl       = finished.reduce((s, p) => s + (p.profitLoss || 0), 0)
  const stake    = finished.reduce((s, p) => s + (p.stake || 0), 0)
  const roi      = stake > 0 ? ((pl / stake) * 100).toFixed(1) : null

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ width: '100%', maxWidth: 430, margin: '0 auto', background: C.bk2, borderRadius: '16px 16px 0 0', padding: '20px 16px 40px', border: `1px solid ${C.border}`, maxHeight: '80vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 18 }}>{market} Breakdown</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.dim, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
          {[
            { l: 'Record', v: finished.length ? `${wins}-${finished.length - wins}` : '—', c: C.blue },
            { l: 'P/L',    v: finished.length ? `${pl >= 0 ? '+' : ''}$${Math.abs(pl).toFixed(0)}` : '—', c: pl >= 0 ? C.green : C.red },
            { l: 'ROI',    v: roi != null ? `${roi}%` : '—', c: parseFloat(roi) >= 0 ? C.green : C.red },
          ].map((s, i) => (
            <div key={i} style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 9, padding: '10px 8px', textAlign: 'center' }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 16, fontWeight: 700, color: s.c }}>{s.v}</div>
              <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{s.l}</div>
            </div>
          ))}
        </div>
        {finished.length === 0
          ? <div style={{ textAlign: 'center', padding: '24px', color: C.dim, fontSize: 12 }}>No settled plays for this market yet</div>
          : finished.slice(0, 10).map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderBottom: `1px solid ${C.dim2}` }}>
              <span style={{ fontSize: 16 }}>{p.status === 'won' ? '✅' : '❌'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{p.playerName}</div>
                <div style={{ fontSize: 10, color: C.dim }}>{p.result || p.team || '—'}</div>
              </div>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: 16, color: p.status === 'won' ? C.green : C.red }}>
                {p.profitLoss != null ? `${p.profitLoss >= 0 ? '+' : ''}$${Math.abs(p.profitLoss).toFixed(2)}` : '—'}
              </div>
            </div>
          ))}
      </div>
    </div>
  )
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: C.bk3, border: `1px solid ${C.blueBdr}`, borderRadius: 8, padding: '8px 12px', fontSize: 11 }}>
      <div style={{ color: C.dim, marginBottom: 2 }}>{label}</div>
      <div style={{ color: C.blue, fontWeight: 600 }}>Hit Rate: {payload[0]?.value}%</div>
    </div>
  )
}

// ── 2-Leg Parlay Record ─────────────────────────────────────────────────────
function TwoLegRecord() {
  const [since, setSince]   = useState('')        // '' = full season
  const [data,  setData]    = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getTwoLegRecord(since)
      .then(d => { if (!cancelled) setData(d) })
      .catch(() => { if (!cancelled) setData(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [since])

  const sp = data?.spread || {}
  const bt = data?.best_team || {}
  const daily = (data?.daily || []).filter(d => d.spread2).slice().reverse()  // newest first
  const rec = (d) => (d && d.n) ? `${d.wins}-${d.n - d.wins}` : '—'
  const pct = (d) => (d && d.n) ? `${Math.round((d.rate || 0) * 100)}%` : ''

  return (
    <>
      <div style={{ padding: '0 16px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 11, color: C.dim, letterSpacing: '.1em', textTransform: 'uppercase' }}>
          🎯 2-Leg Parlay Record
        </span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button onClick={() => setSince('')} style={{
            padding: '3px 9px', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer',
            fontFamily: "'Rajdhani', sans-serif",
            border: `1px solid ${since === '' ? C.green : C.border}`,
            background: since === '' ? `${C.green}18` : 'none', color: since === '' ? C.green : C.dim,
          }}>Season</button>
          <input
            type="date"
            value={since}
            max={todayStr()}
            onChange={e => setSince(e.target.value)}
            style={{
              padding: '3px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
              fontFamily: "'IBM Plex Mono', monospace",
              border: `1px solid ${since ? C.green : C.border}`,
              background: since ? `${C.green}14` : C.bk3,
              color: since ? C.green : C.dim, colorScheme: 'dark',
            }}
          />
        </div>
      </div>

      <div style={{ padding: '0 16px 16px' }}>
        <div style={{ background: C.bk2, border: `1px solid ${C.green}33`, borderRadius: 12, overflow: 'hidden' }}>
          {loading && <div style={{ padding: '18px', textAlign: 'center', color: C.dim, fontSize: 11 }}>Loading…</div>}
          {!loading && !data && <div style={{ padding: '18px', textAlign: 'center', color: C.dim, fontSize: 11 }}>Record unavailable</div>}
          {!loading && data && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: `1px solid ${C.dim2}` }}>
                <div style={{ padding: '12px 14px', borderRight: `1px solid ${C.dim2}` }}>
                  <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>Best 2 picks</div>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 22, color: C.green, lineHeight: 1.1 }}>{rec(sp)}</div>
                  <Mono size={9} color={C.dim}>{pct(sp)} win rate</Mono>
                </div>
                <div style={{ padding: '12px 14px' }}>
                  <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>Best same-team pair</div>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 22, color: C.green, lineHeight: 1.1 }}>{rec(bt)}</div>
                  <Mono size={9} color={C.dim}>{pct(bt)} win rate</Mono>
                </div>
              </div>

              {daily.length === 0 && (
                <div style={{ padding: '14px', textAlign: 'center', color: C.dim, fontSize: 11 }}>No settled days in this window yet</div>
              )}
              {daily.map((d, i) => {
                const s2 = d.spread2
                return (
                  <div key={d.date || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: i < daily.length - 1 ? `1px solid ${C.dim2}` : 'none' }}>
                    <span style={{ fontSize: 14 }}>{s2.won ? '✅' : '❌'}</span>
                    <Mono size={10} color={C.dim}>{fmtChartDate(d.date)}</Mono>
                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <Mono size={10} color={C.text}>
                        {s2.legs.map(l => `${l.team} ${l.hit ? '✓' : '✗'}`).join('  +  ')}
                      </Mono>
                    </div>
                  </div>
                )
              })}
              <div style={{ padding: '7px 14px', background: C.bk3 }}>
                <Mono size={8.5} color={C.dim}>
                  Auto-updates as games settle nightly. Best 2 model picks (any team); same-team pair hits more often (correlation).
                  {daily.length ? ` Updated through ${fmtChartDate(daily[0].date)}.` : ''}
                </Mono>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default function TrackerTab() {
  const [drillMarket, setDrillMarket] = useState(null)
  const [modelStats,  setModelStats]  = useState([])
  const [trend,       setTrend]       = useState([])
  const [loading,     setLoading]     = useState(true)

  useEffect(() => {
    let cancelled = false
    Promise.all([getScorecardSummary(30), getScorecardTrend(14, 'HRR_over_1.5')])
      .then(([sum, tr]) => {
        if (cancelled) return
        const stats = (sum.markets || []).map(m => {
          const wins = m.top10_wins || 0
          const loss = m.top10_losses || 0
          const winPct = wins + loss > 0 ? Math.round((wins / (wins + loss)) * 100) : 0
          return {
            market:  PROP_LABEL[m.prop_type] || m.prop_type,
            record:  `${wins}-${loss}`,
            roi:     fmtRoi(m.roi_top_10),
            roiVal:  (m.roi_top_10 || 0) * 100,
            winPct,
          }
        })
        setModelStats(stats)
        setTrend((tr.series || []).map(s => ({
          d:   fmtChartDate(s.date),
          pct: Math.round((s.hit_rate || 0) * 100),
        })))
      })
      .catch(() => { if (!cancelled) { setModelStats([]); setTrend([]) } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const heroModelRoi = modelStats.find(m => m.market === 'HRR')?.roi || '—'

  const plays    = loadPlays()
  const finished = plays.filter(p => ['won','lost','push'].includes(p.status))
  const wins     = finished.filter(p => p.status === 'won').length
  const totalPL  = finished.reduce((s, p) => s + (p.profitLoss || 0), 0)
  const totalStake = finished.reduce((s, p) => s + (p.stake || 0), 0)
  const winRate  = finished.length > 0 ? ((wins / finished.length) * 100).toFixed(0) : '—'
  const personalROI = totalStake > 0 ? ((totalPL / totalStake) * 100).toFixed(1) : null

  // Build personal market breakdown from real plays
  const marketMap = {}
  finished.forEach(p => {
    const m = p.market || 'Other'
    if (!marketMap[m]) marketMap[m] = { wins: 0, total: 0, pl: 0 }
    marketMap[m].total++
    if (p.status === 'won') marketMap[m].wins++
    marketMap[m].pl += (p.profitLoss || 0)
  })
  const personalMarkets = Object.entries(marketMap).sort((a, b) => b[1].total - a[1].total)

  return (
    <div style={{ paddingBottom: 84 }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: `linear-gradient(180deg, ${C.bk} 85%, transparent)`, padding: '16px 16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: C.blue, fontSize: 20 }}>◆</span>
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 22 }}>My Betting Tracker</span>
          </div>
          <Mono size={10} color={C.dim}>SINCE 4/11</Mono>
        </div>
      </div>

      {/* Hero stats */}
      <div style={{ padding: '0 16px 16px', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
        {[
          { label: 'Model ROI',    val: heroModelRoi, sub: 'HRR Top 10 · 30d',                                            color: heroModelRoi.startsWith('-') ? C.red : C.green },
          { label: 'My P/L',      val: finished.length > 0 ? `${totalPL >= 0 ? '+' : ''}$${Math.abs(totalPL).toFixed(0)}` : '—', sub: `${finished.length} settled`, color: totalPL >= 0 ? C.green : C.red },
          { label: 'My Win Rate', val: winRate !== '—' ? `${winRate}%` : '—',                                              sub: `${wins}-${finished.length - wins}`,  color: C.blue },
        ].map((s, i) => (
          <div key={i} style={{ background: C.bk2, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 8px' }}>
            <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 3 }}>{s.label}</div>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 18, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 9, color: C.dim, marginTop: 2 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ROI chart */}
      <div style={{ padding: '0 16px 16px' }}>
        <div style={{ background: C.bk2, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 8px 10px' }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 11, color: C.dim, letterSpacing: '.08em', textTransform: 'uppercase', padding: '0 8px 10px' }}>Model HRR Hit Rate %</div>
          <ResponsiveContainer width="100%" height={130}>
            <AreaChart data={trend} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
              <defs>
                <linearGradient id="trackerGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={C.blue} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={C.blue} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="d" tick={{ fill: C.dim, fontSize: 9, fontFamily: 'IBM Plex Mono' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: C.dim, fontSize: 9, fontFamily: 'IBM Plex Mono' }} tickLine={false} axisLine={false} domain={[0, 110]} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="pct" stroke={C.blue} strokeWidth={2} fill="url(#trackerGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 2-leg parlay record */}
      <TwoLegRecord />

      {/* Model market breakdown */}
      <div style={{ padding: '0 16px 6px', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 11, color: C.dim, letterSpacing: '.1em', textTransform: 'uppercase' }}>
        Model Market Performance
      </div>
      <div style={{ padding: '0 16px 16px' }}>
        {!loading && modelStats.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: C.dim, fontSize: 11 }}>No model performance data yet</div>
        )}
        {modelStats.map((m, i) => (
          <div key={i} onClick={() => setDrillMarket(m.market)} style={{ background: C.bk2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 15 }}>{m.market}</div>
              <div style={{ marginTop: 5 }}>
                <Track pct={m.winPct} color={m.roiVal >= 0 ? C.blue : C.red} height={4} />
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: 16, color: m.roiVal >= 0 ? C.green : C.red }}>{m.roi}</div>
              <div style={{ fontSize: 9, color: C.dim, fontFamily: "'IBM Plex Mono', monospace" }}>{m.record}</div>
            </div>
            <div style={{ color: C.dim, fontSize: 12 }}>›</div>
          </div>
        ))}
      </div>

      {/* Personal breakdown */}
      {personalMarkets.length > 0 && (
        <>
          <div style={{ padding: '0 16px 6px', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 11, color: C.dim, letterSpacing: '.1em', textTransform: 'uppercase' }}>My Market Breakdown</div>
          <div style={{ padding: '0 16px 16px' }}>
            {personalMarkets.map(([market, s], i) => {
              const wr = s.total > 0 ? Math.round((s.wins / s.total) * 100) : 0
              return (
                <div key={i} onClick={() => setDrillMarket(market)} style={{ background: C.bk2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 14 }}>{market}</div>
                    <div style={{ marginTop: 5 }}><Track pct={wr} color={s.pl >= 0 ? C.blue : C.red} height={4} /></div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: 15, color: s.pl >= 0 ? C.green : C.red }}>{s.pl >= 0 ? '+' : ''}${Math.abs(s.pl).toFixed(0)}</div>
                    <div style={{ fontSize: 9, color: C.dim, fontFamily: "'IBM Plex Mono', monospace" }}>{s.wins}-{s.total - s.wins}</div>
                  </div>
                  <div style={{ color: C.dim, fontSize: 12 }}>›</div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* Personal recent results */}
      {finished.length > 0 && (
        <>
          <div style={{ padding: '0 16px 6px', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 11, color: C.dim, letterSpacing: '.1em', textTransform: 'uppercase' }}>My Recent Results</div>
          <div style={{ padding: '0 16px' }}>
            {finished.slice(0, 8).map((p, i) => (
              <div key={i} style={{ background: C.bk2, border: `1px solid ${p.status === 'won' ? `${C.green}33` : `${C.red}22`}`, borderRadius: 10, padding: '10px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 16 }}>{p.status === 'won' ? '✅' : '❌'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{p.playerName}</div>
                  <div style={{ fontSize: 10, color: C.dim }}>{p.market} · {p.result || '—'}</div>
                </div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: 16, color: p.status === 'won' ? C.green : C.red }}>
                  {p.profitLoss != null ? `${p.profitLoss >= 0 ? '+' : ''}$${Math.abs(p.profitLoss).toFixed(2)}` : '—'}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {finished.length === 0 && personalMarkets.length === 0 && (
        <div style={{ padding: '40px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>◆</div>
          <div style={{ fontSize: 14, fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, color: C.dim }}>No settled plays yet</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>Save plays in My Plays tab and settle them to track ROI</div>
        </div>
      )}

      {drillMarket && <MarketDrilldown market={drillMarket} plays={plays} onClose={() => setDrillMarket(null)} />}
    </div>
  )
}
